import { useState } from 'react'
import { format } from 'date-fns'
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import BeatLoader from 'react-spinners/BeatLoader'

import { query } from '../api'
import { BUILD_ENV, classNames, extractErrorMessage } from '../helper'
import { useNotificationContext } from '../context/notification-context'
import {
  REPORT_LANGUAGES,
  getReportLinks,
  mergeSpaceMatches
} from '../helper/reportLink'

// Ein Lookup, der fehlschlägt, soll die anderen nicht mitreißen: Eine
// Partner-User-ID ist z. B. keine gültige Space-ID, getSpace liefert dann
// einfach null.
const settle = async (promise) => {
  try {
    return await promise
  } catch (err) {
    console.error(err) // eslint-disable-line
    return null
  }
}

const findSpaces = async (id) => {
  const [bySpaceId, byOwner, byPartnerUserId] = await Promise.all([
    settle(query({ query: 'getSpace', variables: { id } })),
    settle(query({ query: 'spacesByOwner', variables: { owner: id } })),
    settle(
      query({
        query: 'spacesByPartnerUserIDByPartner',
        variables: { partner_user_id: id }
      })
    )
  ])

  return mergeSpaceMatches({ bySpaceId, byOwner, byPartnerUserId })
}

const loadById = async (queryName, ids) => {
  const unique = [...new Set(ids.filter(Boolean))]
  const items = await Promise.all(
    unique.map((id) => settle(query({ query: queryName, variables: { id } })))
  )

  return Object.fromEntries(unique.map((id, index) => [id, items[index]]))
}

const LinkRow = ({ label, description, link }) => {
  const { success, error } = useNotificationContext()

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      success(`${label} kopiert`)
    } catch (_) {
      error('Kopieren hat nicht geklappt')
    }
  }

  return (
    <div>
      <div className='flex items-baseline justify-between'>
        <span className='text-sm font-semibold text-gray-900'>{label}</span>
        <span className='text-xs text-gray-500'>{description}</span>
      </div>
      <div className='mt-1 flex gap-2'>
        <input
          readOnly
          value={link}
          onFocus={(event) => event.target.select()}
          className='block w-full rounded-md border-0 py-1.5 text-xs text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
        />
        <button
          type='button'
          onClick={copy}
          title='Kopieren'
          className='shrink-0 rounded-md bg-white px-2.5 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
        >
          <ClipboardDocumentIcon className='h-5 w-5 text-gray-700' />
        </button>
        <a
          href={link}
          target='_blank'
          rel='noreferrer'
          title='Öffnen'
          className='shrink-0 flex items-center rounded-md bg-white px-2.5 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
        >
          <ArrowTopRightOnSquareIcon className='h-5 w-5 text-gray-700' />
        </a>
      </div>
    </div>
  )
}

const SpaceResult = ({ match, partner, career, careerId, language }) => {
  const { space, matchedVia } = match
  const links = getReportLinks({ env: BUILD_ENV, space, careerId, language })

  return (
    <div className='rounded-lg bg-white p-6 shadow-sm'>
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <h3 className='text-base font-semibold text-gray-900'>
            {partner?.display_name || partner?.name || space.partner_id || '—'}
          </h3>
          <p className='mt-1 text-xs text-gray-500'>
            Space {space.id}
            {space.createdAt &&
              ` · angelegt ${format(new Date(space.createdAt), 'dd.MM.yyyy')}`}
            {space.context && ` · ${space.context}`}
            {` · ${space.progress ?? 0} %`}
          </p>
          {space.partner_user_id && (
            <p className='mt-0.5 text-xs text-gray-500'>
              Partner-User-ID {space.partner_user_id}
            </p>
          )}
        </div>
        <div className='flex gap-1.5'>
          {matchedVia.map((via) => (
            <span
              key={via}
              className='rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700'
            >
              gefunden über {via}
            </span>
          ))}
        </div>
      </div>

      {(space.progress ?? 0) < 100 && (
        <p className='mt-4 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800'>
          Assessment ist nicht abgeschlossen, der Report kann leer oder
          unvollständig sein.
        </p>
      )}

      <div className='mt-4 space-y-4'>
        <LinkRow
          label='Stärkenprofil'
          description={`TALENTPROFILE · ${links.language}`}
          link={links.talentProfile}
        />
        {links.matchingProfile ? (
          <LinkRow
            label='Passungsprofil'
            description={`${career?.title || links.careerId}${
              careerId ? ' (eingegeben)' : ''
            } · ${links.language}`}
            link={links.matchingProfile}
          />
        ) : (
          <p className='text-xs text-gray-500'>
            Kein Passungsprofil: Am Space hängt keine Stelle. Für ein
            Passungsprofil oben eine Career-ID eingeben.
          </p>
        )}
      </div>
    </div>
  )
}

const ReportLink = () => {
  const [id, setId] = useState('')
  const [careerId, setCareerId] = useState('')
  const [language, setLanguage] = useState('')
  const [state, setState] = useState({ status: 'idle' })

  const search = async (event) => {
    event.preventDefault()
    const trimmedId = id.trim()
    const trimmedCareerId = careerId.trim()
    if (!trimmedId) return

    setState({ status: 'loading' })
    try {
      const matches = await findSpaces(trimmedId)
      const [partners, careers] = await Promise.all([
        loadById(
          'getPartner',
          matches.map(({ space }) => space.partner_id)
        ),
        loadById('getCareer', [
          trimmedCareerId,
          ...matches.map(({ space }) => space.career_id)
        ])
      ])

      setState({
        status: 'done',
        matches,
        partners,
        careers,
        careerId: trimmedCareerId,
        language
      })
    } catch (err) {
      setState({ status: 'error', message: extractErrorMessage(err) })
    }
  }

  const enteredCareerMissing =
    state.status === 'done' && state.careerId && !state.careers[state.careerId]

  return (
    <div className='max-w-3xl'>
      <header className='mb-8'>
        <h1 className='text-3xl font-bold leading-tight tracking-tight text-gray-900'>
          Report-Link
        </h1>
        <p className='mt-2 text-sm text-gray-500'>
          Stärkenprofil- und Passungsprofil-Links zu einem Talent. Eingabe:
          User-ID, Space-ID oder Partner-User-ID. Umgebung: {BUILD_ENV}.
        </p>
      </header>

      <form
        onSubmit={search}
        className='grid grid-cols-1 gap-4 rounded-lg bg-white p-6 shadow-sm sm:grid-cols-6'
      >
        <div className='sm:col-span-6'>
          <label
            htmlFor='talent_id'
            className='block text-sm font-medium text-gray-900'
          >
            User-ID, Space-ID oder Partner-User-ID
          </label>
          <input
            id='talent_id'
            autoFocus
            value={id}
            onChange={(event) => setId(event.target.value)}
            className='mt-1 block w-full rounded-md border-0 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
          />
        </div>
        <div className='sm:col-span-4'>
          <label
            htmlFor='career_id'
            className='block text-sm font-medium text-gray-900'
          >
            Career-ID <span className='text-gray-400'>(optional)</span>
          </label>
          <input
            id='career_id'
            value={careerId}
            onChange={(event) => setCareerId(event.target.value)}
            placeholder='sonst die Stelle am Space'
            className='mt-1 block w-full rounded-md border-0 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
          />
        </div>
        <div className='sm:col-span-2'>
          <label
            htmlFor='language'
            className='block text-sm font-medium text-gray-900'
          >
            Sprache
          </label>
          <select
            id='language'
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className='mt-1 block w-full rounded-md border-0 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
          >
            <option value=''>wie das Talent</option>
            {REPORT_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
        <div className='sm:col-span-6 flex justify-end'>
          <button
            type='submit'
            disabled={!id.trim() || state.status === 'loading'}
            className='rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50'
          >
            Links erzeugen
          </button>
        </div>
      </form>

      <div className='mt-8 space-y-4'>
        {state.status === 'loading' && (
          <div className='flex justify-center py-8'>
            <BeatLoader color='#374151' size={12} />
          </div>
        )}
        {state.status === 'error' && (
          <p className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-800'>
            {state.message}
          </p>
        )}
        {enteredCareerMissing && (
          <p className='rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800'>
            Zur Career-ID {state.careerId} gibt es keine Stelle. Der Link wird
            trotzdem gebaut, der Report bleibt aber leer.
          </p>
        )}
        {state.status === 'done' && state.matches.length === 0 && (
          <p className='rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700'>
            Kein Space zu dieser ID gefunden (weder als Space-ID, User-ID noch
            Partner-User-ID).
          </p>
        )}
        {state.status === 'done' &&
          state.matches.map((match) => (
            <SpaceResult
              key={match.space.id}
              match={match}
              partner={state.partners[match.space.partner_id]}
              career={state.careers[state.careerId || match.space.career_id]}
              careerId={state.careerId}
              language={state.language}
            />
          ))}
        {state.status === 'done' && state.matches.length > 0 && (
          <p
            className={classNames(
              'text-xs text-gray-500',
              state.matches.length > 1 ? 'pt-2' : ''
            )}
          >
            Die Links sind nicht signiert und laufen nicht ab: Wer den Link hat,
            sieht den Report. Nur gezielt weitergeben.
          </p>
        )}
      </div>
    </div>
  )
}

export default ReportLink
